import * as React from "react";

import "./ComponentTiles.scss";

import { IComponentCategory, IComponentDefinition } from "../Components/PageDefinitionTypes";
import { ITile, TileCollection, TileImageSize } from "../Components/TileCollection";

import { ObservableValue } from "azure-devops-ui/Core/Observable";
import * as Util_Accessibility from "azure-devops-ui/Core/Util/Accessibility";
import { format } from "azure-devops-ui/Core/Util/String";
import { TextField } from "azure-devops-ui/TextField";
import { getSafeId } from "azure-devops-ui/Util";

const tilesPerRow = 4;

export interface IComponentTilesProps {
    categories: IComponentCategory[];
    components: IComponentDefinition[];
    searchPlaceholder: string;
}

export class ComponentTiles extends React.Component<IComponentTilesProps> {
    private searchValue = new ObservableValue("");
    private tileCount = 0;

    public render(): JSX.Element | null {
        let searchTerms: string[] | undefined;
        if (this.searchValue.value.trim()) {
            searchTerms = this.searchValue.value.trim().toLocaleLowerCase().split(" ");
        }
        this.tileCount = 0;
        const tiles = this.props.categories.map((c) => this.renderTilesForCategory(c, searchTerms)).filter((tile) => tile !== null);

        if (this.tileCount) {
            Util_Accessibility.announce(format("showing {0} components", this.tileCount));
        }

        return (
            <div className="component-tiles">
                <TextField
                    ariaControls="search-results"
                    className="component-tiles-filter"
                    value={this.searchValue}
                    ariaLabel="Filter components"
                    onChange={this.onTextChange}
                    placeholder={this.props.searchPlaceholder}
                    prefixIconProps={{ iconName: "Search" }}
                />
                <div id={getSafeId("search-results")}>
                    <div role="status">{!tiles.length && "There are no items matching keyword: " + this.searchValue.value}</div>
                    {tiles.length ? tiles : null}
                </div>
            </div>
        );
    }

    private renderTilesForCategory(category: IComponentCategory, searchTerms: string[] | undefined): JSX.Element | null {
        const tiles: ITile[][] = [];
        let lastRow: ITile[] | undefined;

        this.props.components.forEach((c) => {
            if (c.category !== category.id) {
                return;
            }

            if (searchTerms) {
                const componentName = c.name.toLocaleLowerCase();
                let isMatch = true;

                searchTerms.forEach((s) => {
                    if (s && componentName.indexOf(s) === -1) {
                        isMatch = false;
                    }
                });

                if (!isMatch) {
                    return;
                }
            }

            if (tiles.length === 0 || lastRow!.length === tilesPerRow) {
                lastRow = [];
                tiles.push(lastRow);
            }
            lastRow!.push({
                imageSize: TileImageSize.FourThree,
                imageUrl: c.thumbnailImage,
                title: c.name,
                url: c.url
            });
            this.tileCount++;
        });

        const headerId = `component-tiles-category-header-${category.id}`;

        return tiles.length > 0 ? (
            <div aria-labelledby={headerId} className="component-tiles-category" key={category.id} role="region">
                <h3 className="component-tiles-category-header" id={headerId}>
                    {category.name}
                </h3>
                <TileCollection className="component-tiles-collection" tiles={tiles} />
            </div>
        ) : null;
    }

    private onTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        this.searchValue.value = value;
        this.forceUpdate();
    };
}
